import { Component, ViewChild, ChangeDetectorRef, Injector, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { AlertService } from '../../Services/notifications/alert.service';
import { LoggerService } from '../../Services/logger';
import { ApiService } from '../../Services/api';


interface Appointment { slot: string; agenda: string; createdAt: string; notes?: string; status?: 'pending' | 'approved' | 'rejected' }

@Component({
    selector: 'app-appoint-call',
    standalone: true,
    imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule, MatButtonModule, MatTooltipModule, MatTabsModule, MatBadgeModule],
    templateUrl: './appoint-call.component.html',
    styleUrls: ['./appoint-call.component.scss']
})
export class AppointCallComponent implements OnInit, AfterViewInit {
    @ViewChild('tabGroup') tabGroup: any;
    private suppressTabChange = false;
    displayMonth = new Date().getMonth();
    displayYear = new Date().getFullYear();
    selectedDate: Date | null = null;
    selectedSlot: string | null = null;
    agenda: string = '';
    expanded: Record<string, boolean> = {};//viren
    private blockedMessageShown = false;//viren
    private blockedMessageTimeout: any = null;//viren


    weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    timeSlots: string[] = ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];

    scheduledAppointments: Record<string, Appointment[]> = {};
    private monthsFetched: Set<string> = new Set();

    calendarDays: Array<{ date: Date, otherMonth?: boolean, isPast?: boolean, isWeekend?: boolean, hasSlots?: boolean, hasAppointments?: boolean, appointmentCount?: number, appointmentStatus?: 'pending' | 'approved' | 'none', isToday?: boolean }> = [];

    private alert!: AlertService;
    private logger!: LoggerService;
    private api!: ApiService;

    constructor(private injector: Injector, private cdr: ChangeDetectorRef) {
        this.buildCalendar(this.displayMonth, this.displayYear);
    }

    ngAfterViewInit(): void {
        // Select current date by default on page load (if not past/weekend)
        const today = new Date();
        if (!this.isPastDate(today) && today.getDay() !== 0 && today.getDay() !== 6) {
            // ensure calendar is showing current month/year
            this.displayMonth = today.getMonth();
            this.displayYear = today.getFullYear();
            this.buildCalendar(this.displayMonth, this.displayYear);
            // Prefetch appointments for visible month so badges show on page enter
            try { this.prefetchMonthAppointments(this.displayMonth, this.displayYear); } catch (e) { }
            // run in next tick so view is stable
            setTimeout(() => this.selectDate(today), 0);
        }
    }

    ngOnInit(): void {
        try {
            this.alert = this.injector.get(AlertService as any) as AlertService;
        } catch (e) { /* e */ }
        try {
            this.logger = this.injector.get(LoggerService as any) as LoggerService;
        } catch (e) { /* e */ }
        try {
            this.api = this.injector.get(ApiService as any) as ApiService;
        } catch (e) { /* e */ }
    }

    slotsCols: 1 | 2 = 2;

    forceTwoByThree = true;

    activeTab: 'available' | 'scheduled appointments' = 'available';
    activeTabIndex = 0;

    onTabChange(index: number) {
        if (this.suppressTabChange) return;
        this.activeTabIndex = index;
        // If Available tab is present it will be the first tab (index 0). If it's not present,
        // the Booked tab will be at index 0. Map index accordingly based on current selectedDate.
        const hasAvailable = this.selectedDate ? this.availableSlotsFor(this.selectedDate).length > 0 : false;
        if (hasAvailable) {
            this.activeTab = index === 0 ? 'available' : 'scheduled appointments';
        } else {
            // Only Booked tab is visible; index 0 => booked
            this.activeTab = 'scheduled appointments';
        }
        if (this.activeTab === 'available') this.selectedAppointment = null;
    }

    setSlotsCols(n: 1 | 2) {
        this.slotsCols = n;
    }

    public dateKey(d: Date) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    selectedAppointment: Appointment | null = null;

    bookedAppointmentsFor(d: Date | null) {
        if (!d) return [] as Appointment[];
        return this.scheduledAppointments[this.dateKey(d)] || [];
    }

    filteredBookedAppointmentsFor(d: Date | null) {
        const all = this.bookedAppointmentsFor(d) || [];
        if (!this.selectedSlot) return all;
        return all.filter(a => a.slot !== this.selectedSlot);
    }

    bookedSlotsFor(d: Date | null) {
        return this.bookedAppointmentsFor(d).map(a => a.slot);
    }

    buildCalendar(month: number, year: number) {
        this.calendarDays = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const firstOfMonth = new Date(year, month, 1);
        const startDay = firstOfMonth.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const prevMonthDays = startDay;
        const prevMonthLastDate = new Date(year, month, 0).getDate();
        for (let i = prevMonthDays - 1; i >= 0; i--) {
            const d = new Date(year, month - 1, prevMonthLastDate - i);
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            this.calendarDays.push({ date: d, otherMonth: true, isPast: this.isPastDate(d), isWeekend, isToday: this.isSameDate(d, today) });
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const dateObj = new Date(year, month, d);
            const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
            const key = this.dateKey(dateObj);
            const hasSlots = this.availableSlotsFor(dateObj).length > 0;
            const appts = this.scheduledAppointments[key] || [];
            const hasAppointments = appts.length > 0;
            const appointmentCount = appts.length; //viren
            console.log("appointment coutn", appointmentCount)
            const appointmentStatus = hasAppointments
                ? (appts.some(a => a.status === 'pending') ? 'pending' : (appts.some(a => a.status === 'approved') ? 'approved' : 'none'))
                : 'none';
            this.calendarDays.push({ date: dateObj, otherMonth: false, isPast: this.isPastDate(dateObj), isWeekend, hasSlots, hasAppointments, appointmentStatus, appointmentCount, isToday: this.isSameDate(dateObj, today) });
        }

        while (this.calendarDays.length % 7 !== 0) {
            const nextIndex = this.calendarDays.length - (startDay + daysInMonth) + 1;
            const d = new Date(year, month + 1, nextIndex);
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            const hasSlots = this.availableSlotsFor(d).length > 0;
            const key = this.dateKey(d);
            const appts = this.scheduledAppointments[key] || [];
            const hasAppointments = appts.length > 0;
            const appointmentCount = appts.length; //viren
            console.log("appointment coutn", appointmentCount)

            const appointmentStatus = hasAppointments
                ? (appts.some(a => a.status === 'pending') ? 'pending' : (appts.some(a => a.status === 'approved') ? 'approved' : 'none'))
                : 'none';
            this.calendarDays.push({ date: d, otherMonth: true, isPast: this.isPastDate(d), isWeekend, hasSlots, hasAppointments, appointmentStatus, appointmentCount, isToday: this.isSameDate(d, today) });
        }
    }

    isPastDate(d: Date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const cmp = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        return cmp < today;
    }

    prevMonth() {
        if (this.displayMonth === 0) {
            this.displayMonth = 11; this.displayYear--;
        } else {
            this.displayMonth--;
        }
        this.buildCalendar(this.displayMonth, this.displayYear);
        try { this.prefetchMonthAppointments(this.displayMonth, this.displayYear); } catch (e) { }
    }

    nextMonth() {
        if (this.displayMonth === 11) {
            this.displayMonth = 0; this.displayYear++;
        } else {
            this.displayMonth++;
        }
        this.buildCalendar(this.displayMonth, this.displayYear);
        try { this.prefetchMonthAppointments(this.displayMonth, this.displayYear); } catch (e) { }
    }

    selectDate(d: Date) {
        if (this.isPastDate(d)) return;
        this.selectedDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        this.selectedSlot = null;
        // Default to Available tab when selecting a new date. Delay the programmatic
        // tab selection to the next microtask so that Angular finishes rendering the
        // conditional tabs and MatTabGroup can accept the selectedIndex reliably.
        this.selectedAppointment = null;
        // Determine target index: Available = 0, Booked = 1
        const hasAvailable = this.selectedDate ? this.availableSlotsFor(this.selectedDate).length > 0 : false;
        const targetIndex = hasAvailable ? 0 : 1;
        this.suppressTabChange = true;
        // Set tabGroup index synchronously so the UI reflects the change immediately.
        if (this.tabGroup && typeof this.tabGroup.selectedIndex !== 'undefined') {
            this.tabGroup.selectedIndex = targetIndex;
        }
        this.activeTabIndex = targetIndex;
        this.activeTab = hasAvailable ? 'available' : 'scheduled appointments';
        this.suppressTabChange = false;
        try {
            const key = this.dateKey(this.selectedDate!);
            const iso = this.dateKey(this.selectedDate!);
            this.fetchAppointments(iso);
            try { this.cdr.detectChanges(); } catch (e) { }
        } catch (e) { /* ignore */ }
    }

    isSameDate(a: Date | null, b: Date | null) {
        if (!a || !b) return false;
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    availableSlotsFor(d: Date) {
        if (!d || this.isPastDate(d)) return [];
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        if (isWeekend) return [];

        const key = this.dateKey(d);
        const appts = this.scheduledAppointments[key] || [];
        const bookedSlots = appts.map(a => a.slot);

        const today = new Date();
        if (this.isSameDate(d, today)) {
            const now = new Date();
            return this.timeSlots.filter(s => {
                const slotTime = this.slotTimeFor(d, s);
                return slotTime > now && bookedSlots.indexOf(s) === -1;
            });
        }

        return this.timeSlots.filter(s => bookedSlots.indexOf(s) === -1);
    }

    slotTimeFor(d: Date, slot: string) {
        const parts = slot.trim().split(' ');
        if (parts.length < 2) return new Date(d);
        const time = parts[0];
        const ampm = parts[1].toUpperCase();
        const [hhStr, mmStr] = time.split(':');
        let hh = parseInt(hhStr, 10);
        const mm = parseInt(mmStr || '0', 10);
        if (ampm === 'PM' && hh !== 12) hh += 12;
        if (ampm === 'AM' && hh === 12) hh = 0;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm, 0, 0);
    }

    selectSlot(s: string) {
        this.selectedSlot = s;
    }

    tooltipFor(d: { date: Date, isPast?: boolean, isWeekend?: boolean, hasSlots?: boolean }) {
        if (!d) return '';
        if (d.isPast) return 'Unavailable (Past Date)';
        if (d.isWeekend) return 'Weekend';
        if (d.hasSlots === false) return 'No slots available';
        return '';
    }

    submit() {
        // Only allow submit if date, slot and agenda present
        if (!this.selectedDate || !this.selectedSlot || !this.agenda || !this.agenda.trim()) {
            this.alert.show('Please select date, time slot and enter agenda before scheduling', { type: 'error' });
            return;
        }

        const isoDate = this.dateKey(this.selectedDate);
        const payload = { date: isoDate, slot: this.selectedSlot, agenda: this.agenda.trim(), status: 'pending' };

        this.api.post('/taxpayer/appointments', payload).subscribe({
            next: (resp: any) => {
                try {
                    const data = resp?.data || resp;
                    const key = this.dateKey(this.selectedDate!);
                    if (!this.scheduledAppointments[key]) this.scheduledAppointments[key] = [];
                    const appt: Appointment = { slot: this.selectedSlot!, agenda: this.agenda.trim(), createdAt: new Date().toISOString(), status: data?.status || 'pending' };
                    this.scheduledAppointments[key].push(appt);
                    this.alert.show('Call requested successfully', { type: 'success' });
                    this.logger.info('Appoint call requested', payload, 'appoint-call.component.ts');
                    this.buildCalendar(this.displayMonth, this.displayYear);
                    // Reset fields first so the view can reflect cleared values
                    this.selectedSlot = null;
                    this.agenda = '';
                    this.selectedAppointment = null;
                    this.selectedDate = null;
                    this.activeTabIndex = 0;
                    this.activeTab = 'available';
                    // Trigger change detection after resetting model values
                    try { this.cdr.detectChanges(); } catch (e) { }
                    // If the agenda input is focused, blur it so the cleared value shows immediately
                    try {
                        const el = document.getElementById('agendaInput') as HTMLElement | null;
                        if (el && typeof el['blur'] === 'function') el['blur']();
                    } catch (e) { /* ignore */ }
                } catch (e) {
                    this.alert.show('Scheduled but unable to parse response', { type: 'info' });
                }
            },
            error: (err: any) => {
                const msg = err?.error?.message || err?.message || 'Failed to schedule appointment';
                this.alert.show(msg, { type: 'error' });
            }
        });
    }

    fetchAppointments(isoDate: string) {
        if (!isoDate) return;
        this.api.get(`/taxpayer/appointments?date=${encodeURIComponent(isoDate)}`).subscribe({
            next: (resp: any) => {
                try {
                    const data = resp?.data || resp;
                    const appointments = data?.appointments || data || [];
                    const key = isoDate;
                    this.scheduledAppointments[key] = appointments.map((a: any) => ({ slot: a.slot, agenda: a.agenda, createdAt: a.createdAt, notes: a.notes, status: a.status }));
                    this.buildCalendar(this.displayMonth, this.displayYear);
                    try { this.cdr.detectChanges(); } catch (e) { }
                } catch (e) {
                    this.logger.warn('Unable to read appointments response', e);
                }
            },
            error: (err: any) => {
                this.logger.warn('Failed to fetch appointments for ' + isoDate, err);
            }
        });
    }

    /**
     * Prefetch appointments for all days in the given month (year) and cache results so badges
     * are visible immediately when the calendar is rendered.
     */
    private prefetchMonthAppointments(month: number, year: number) {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
        if (this.monthsFetched.has(monthKey)) return;
        this.monthsFetched.add(monthKey);

        // Prefer fetching the whole month's appointments in a single call if backend supports it.
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

        this.api.get(`/taxpayer/appointments?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`).subscribe({
            next: (resp: any) => {
                try {
                    const data = resp?.data || resp;
                    // Expecting { appointmentsByDate: { 'YYYY-MM-DD': [ ... ] } }
                    if (data && data.appointmentsByDate) {
                        const map = data.appointmentsByDate;
                        for (const key of Object.keys(map)) {
                            this.scheduledAppointments[key] = (map[key] || []).map((a: any) => ({ slot: a.slot, agenda: a.agenda, createdAt: a.createdAt, notes: a.notes, status: a.status }));
                        }
                        this.buildCalendar(this.displayMonth, this.displayYear);
                        try { this.cdr.detectChanges(); } catch (e) { }
                        return;
                    }

                    // Fallback: if server didn't return appointmentsByDate, fall back to per-day fetch
                    this.fetchVisibleMonthDays(month, year);
                } catch (e) {
                    this.logger.warn('Unable to parse month appointments response, falling back to per-day fetch', e);
                    this.fetchVisibleMonthDays(month, year);
                }
            },
            error: (err: any) => {
                // on error, fall back to the previous per-day fetching strategy
                this.logger.warn('Failed to prefetch month appointments, falling back to per-day fetch', err);
        this.fetchVisibleMonthDays(month, year);
    }
        });
    }

    private fetchVisibleMonthDays(month: number, year: number) {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
            const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            // Only fetch if we don't already have data for that day
            if (!this.scheduledAppointments[iso]) {
                this.fetchAppointments(iso);
            }
        }
    }

    reset() {
        this.selectedDate = null; this.selectedSlot = null; this.agenda = '';
        this.selectedAppointment = null;
        this.activeTabIndex = 0;
        this.activeTab = 'available';
    }

    approveAppointment(appt: Appointment, date: Date) {
        appt.status = 'approved';
        this.buildCalendar(this.displayMonth, this.displayYear);
        this.selectedAppointment = appt;
    }

    rejectAppointment(appt: Appointment, date: Date) {
        const key = this.dateKey(date);
        const list = this.scheduledAppointments[key] || [];
        const idx = list.findIndex(a => a.slot === appt.slot && a.createdAt === appt.createdAt);
        if (idx !== -1) {
            list.splice(idx, 1);
            if (list.length === 0) delete this.scheduledAppointments[key];
            else this.scheduledAppointments[key] = list;
        }
        this.buildCalendar(this.displayMonth, this.displayYear);
        this.selectedAppointment = null;
        if (this.selectedSlot === appt.slot) this.selectedSlot = null;
    }
    badgeTooltip(d: { appointmentCount?: number }) {
        const count = d.appointmentCount ?? 0;
        return count > 0
            ? `You have ${count} meeting${count > 1 ? 's' : ''} scheduled`
            : '';
    }
    onBadgeClick(event: MouseEvent, date: Date) {
        event.stopPropagation();
        this.selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        this.activeTabIndex = 1;
        this.activeTab = 'scheduled appointments';
    }

    preventPaste(evt: ClipboardEvent) { //viren
        try {
            const target = evt.target as HTMLElement | null;
            if (target && target.classList.contains('.agenda-input')) return;
            evt.preventDefault();
            evt.stopPropagation();
            this.showBlockedClipboardMessage();
        } catch (e) { /* ignore */ }
    }

    preventCopy(evt: ClipboardEvent) { //viren
        try { evt.preventDefault(); evt.stopPropagation(); } catch (e) { }
    }

    preventCut(evt: ClipboardEvent) { //viren
        try { evt.preventDefault(); evt.stopPropagation(); } catch (e) { }
    }


    private showBlockedClipboardMessage() { //viren
        try {
            if (this.blockedMessageShown) return;
            this.blockedMessageShown = true;
            if (this.alert && typeof this.alert.show === 'function') {
                this.alert.show("This field doesn't allow copy or paste for security reasons.", { type: 'info', duration: 4000 });
            } else {
                try { alert("This field doesn't allow copy or paste for security reasons."); } catch (e) { }
            }
            if (this.blockedMessageTimeout) clearTimeout(this.blockedMessageTimeout);
            this.blockedMessageTimeout = setTimeout(() => { this.blockedMessageShown = false; this.blockedMessageTimeout = null; }, 3000);
        } catch (e) { /* ignore */ }
    }


    toggleAgenda(createdAt: string) {//viren
        this.expanded[createdAt] = !this.expanded[createdAt];
    }

}